import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  CustomerBuyerInventoryState,
  CustomerBuyerStockItemsQuery,
  FiltersGroupedStockItemQuery,
  GroupedStockItemQuery
} from "../shared/models/customerBuyerStockItemsModels";
import customerBuyerStockItemsApi from "../shared/api/customerBuyerStockItemsApi";

export const getStockItems = createAsyncThunk(
  "customerBuyerInventory/getStockItems",
  async (query: CustomerBuyerStockItemsQuery) => {
    return customerBuyerStockItemsApi.getStockItems(query);
  }
);

export const getStockItem = createAsyncThunk("customerBuyerInventory/getStockItem", async (id: string) => {
  return customerBuyerStockItemsApi.getStockItem(id);
});

export const getGroupedStockItems = createAsyncThunk(
  "customerBuyerInventory/getGroupedStockItems",
  async (query: GroupedStockItemQuery) => {
    return customerBuyerStockItemsApi.getGroupedStockItems(query);
  }
);

export const getFilters = createAsyncThunk(
  "customerBuyerInventory/getFilters",
  async (query: FiltersGroupedStockItemQuery) => {
    return customerBuyerStockItemsApi.getFiltersGroupedStockItems(query);
  }
);

const initialState: CustomerBuyerInventoryState = {
  query: {
    conditionCodes: undefined,
    manufacturerName: undefined,
    number: undefined,
    search: undefined,
    sortBy: undefined,
    sortDesc: true,
    pageNo: 1,
    pageSize: 20
  },
  stockItems: undefined,
  stockItem: undefined,
  groupedStockItems: { items: [], pageNo: 0, pageSize: 0, totalCount: 0, totalPages: 0 },
  groupedStockItemsQuery: {
    number: undefined,
    manufacturerIds: [],
    conditionCodes: [],
    countryIds: [],
    categoryIds: [],
    pageNo: 0,
    pageSize: 0
  },
  filters: {
    countries: [],
    manufacturers: [],
    categories: [],
    conditions: [],
    groups: []
  },
  filtersQuery: {
    number: undefined,
    manufacturerIds: [],
    categoryIds: [],
    conditionCodes: [],
    countryIds: [],
    groupIds: []
  },
  stockItemsUploadResult: undefined,
  manufacturers: [],
  categories: [],
  loading: false,
  filterLoading: false,
  success: undefined,
  error: undefined
};

const customerBuyerInventorySlice = createSlice({
  name: "customerBuyerInventory",
  initialState: initialState,
  reducers: {
    changeQuery: (state, action) => {
      state.query = action.payload;
    },
    setStockItems: (state, { payload }) => {
      state.stockItems = payload;
    },
    setGroupedStockItems: (state, { payload }) => {
      state.groupedStockItems = payload;
    },
    changeGroupedStockItemsQuery: (state, action) => {
      state.groupedStockItemsQuery = action.payload;
    },
    changeProductFiltersQuery: (state, action) => {
      state.filtersQuery = action.payload;
    },
    selectStockItem: (state, { payload }) => {
      state.stockItem = payload;
    },
    setSuccess: (state, action) => {
      state.success = action.payload;
    },
    setError: (state, { payload }) => {
      state.error = payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getStockItems.pending, (state) => {
        state.loading = true;
      })
      .addCase(getStockItems.fulfilled, (state, { payload }) => {
        state.stockItems = payload;
        state.loading = false;
      })
      .addCase(getStockItems.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getStockItem.pending, (state) => {
        state.loading = true;
      })
      .addCase(getStockItem.fulfilled, (state, { payload }) => {
        state.stockItem = payload;
        state.loading = false;
      })
      .addCase(getStockItem.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getGroupedStockItems.pending, (state) => {
        state.loading = true;
      })
      .addCase(getGroupedStockItems.fulfilled, (state, { payload }) => {
        if (payload.items.length < 1) {
          state.loading = false;
        }

        if (state.groupedStockItems.pageNo !== payload.pageNo) {
          state.groupedStockItems = { ...payload, items: state.groupedStockItems.items.concat(payload.items) };
        }
        state.loading = false;
      })
      .addCase(getGroupedStockItems.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getFilters.pending, (state) => {
        state.filterLoading = true;
      })
      .addCase(getFilters.fulfilled, (state, { payload }) => {
        state.filters = payload;
        state.filterLoading = false;
      })
      .addCase(getFilters.rejected, (state, { error }) => {
        state.error = error;
        state.filterLoading = false;
      });
  }
});

export const {
  changeQuery,
  setStockItems,
  changeGroupedStockItemsQuery,
  changeProductFiltersQuery,
  setGroupedStockItems,
  selectStockItem,
  setSuccess,
  setError
} = customerBuyerInventorySlice.actions;

export default customerBuyerInventorySlice.reducer;